import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { 
  Search, 
  Filter,
  MoreVertical,
  Eye,
  UserX,
  UserCheck,
  ArrowUpDown,
  ExternalLink,
  Trash2,
  AlertTriangle,
  HelpCircle,
  Crown
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../../components/ui/dropdown-menu";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../../components/ui/tooltip";
import { toast } from "sonner";
import axios from "axios";
import { API } from "../../App";

const AdminPhotographers = () => {
  const [photographers, setPhotographers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [planFilter, setPlanFilter] = useState("all");
  const [selectedPhotographer, setSelectedPhotographer] = useState(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [actionDialogOpen, setActionDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [founderDialogOpen, setFounderDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [actionType, setActionType] = useState("");
  const [actionData, setActionData] = useState({ plan_key: "", reason: "" });
  const [founderTier, setFounderTier] = useState("none");
  const [savingFounder, setSavingFounder] = useState(false);

  useEffect(() => {
    fetchPhotographers();
  }, [statusFilter, planFilter]);

  const fetchPhotographers = async () => {
    try {
      const token = localStorage.getItem("admin_token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      const params = new URLSearchParams();
      if (statusFilter !== "all") params.append("status", statusFilter);
      if (planFilter !== "all") params.append("plan", planFilter);
      
      const response = await axios.get(
        `${API}/admin/photographers?${params.toString()}`,
        { headers }
      );
      setPhotographers(response.data.photographers);
    } catch (error) {
      toast.error("Failed to load photographers");
    } finally {
      setLoading(false);
    }
  };

  const handleAction = async () => {
    if (!selectedPhotographer) return;

    try {
      const token = localStorage.getItem("admin_token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      await axios.put(
        `${API}/admin/photographers/${selectedPhotographer.photographer_id}/status`,
        {
          status: actionType,
          plan_key: actionData.plan_key || undefined,
          reason: actionData.reason || undefined
        },
        { headers }
      );

      toast.success(`Photographer ${actionType === "suspended" ? "suspended" : "updated"}`);
      setActionDialogOpen(false);
      setActionData({ plan_key: "", reason: "" });
      fetchPhotographers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Action failed");
    }
  };

  const handleImpersonate = async (photographer) => {
    try {
      const token = localStorage.getItem("admin_token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      const response = await axios.post(
        `${API}/admin/photographers/${photographer.photographer_id}/impersonate`,
        {},
        { headers }
      );

      // Open dashboard in new tab with impersonation token
      const url = `/dashboard?impersonate=${response.data.session_token}`;
      window.open(url, "_blank");
      toast.success("Impersonation session created (1 hour)");
    } catch (error) {
      toast.error("Failed to create impersonation session");
    }
  };

  const handleDelete = async () => {
    if (!selectedPhotographer) return;
    
    setDeleting(true);
    try {
      const token = localStorage.getItem("admin_token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      const response = await axios.delete(
        `${API}/admin/photographers/${selectedPhotographer.photographer_id}`,
        { headers }
      );
      
      toast.success("Account permanently deleted");
      setDeleteDialogOpen(false);
      setSelectedPhotographer(null);
      fetchPhotographers();
      
      // Show summary of what was deleted
      const counts = response.data.deleted_counts;
      console.log("Deleted:", counts);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete account");
    } finally {
      setDeleting(false);
    }
  };

  const handleFounderStatus = async () => {
    if (!selectedPhotographer) return;
    
    setSavingFounder(true);
    try {
      const token = localStorage.getItem("admin_token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      if (founderTier === "none") {
        // Remove founder status
        await axios.delete(
          `${API}/usage/admin/founder/${selectedPhotographer.photographer_id}`,
          { headers }
        );
        toast.success("Founder status removed");
      } else {
        // Set founder status
        await axios.post(
          `${API}/usage/admin/founder/${selectedPhotographer.photographer_id}?tier=${founderTier}`,
          {},
          { headers }
        );
        toast.success(`Founder status set to ${founderTier.replace("_", " ")}`);
      }
      
      setFounderDialogOpen(false);
      fetchPhotographers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update founder status");
    } finally {
      setSavingFounder(false);
    }
  };

  const openFounderDialog = (photographer) => {
    setSelectedPhotographer(photographer);
    setFounderTier(photographer.founder_tier || "none");
    setFounderDialogOpen(true);
  };

  const getStatusBadge = (status) => {
    const badges = {
      trialing: { label: "Trial", className: "bg-blue-500/20 text-blue-400 border-blue-500/30" },
      active: { label: "Active", className: "bg-green-500/20 text-green-400 border-green-500/30" },
      past_due: { label: "Past Due", className: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30" },
      canceled: { label: "Canceled", className: "bg-gray-500/20 text-gray-400 border-gray-500/30" },
      suspended: { label: "Suspended", className: "bg-red-500/20 text-red-400 border-red-500/30" }
    };
    return badges[status] || badges.trialing;
  };

  const filteredPhotographers = photographers.filter(p => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      p.user?.name?.toLowerCase().includes(query) ||
      p.user?.email?.toLowerCase().includes(query)
    );
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="admin-photographers">
      <div>
        <h1 className="text-3xl md:text-4xl tracking-tight mb-2">Photographer Management</h1>
        <p className="text-muted-foreground">Manage all photographer accounts</p>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search by name or email..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
            data-testid="search-photographers"
          />
        </div>
        
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-40" data-testid="status-filter">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="trialing">Trialing</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="past_due">Past Due</SelectItem>
            <SelectItem value="canceled">Canceled</SelectItem>
            <SelectItem value="suspended">Suspended</SelectItem>
          </SelectContent>
        </Select>

        <Select value={planFilter} onValueChange={setPlanFilter}>
          <SelectTrigger className="w-40" data-testid="plan-filter">
            <SelectValue placeholder="Plan" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Plans</SelectItem>
            <SelectItem value="starter">Starter</SelectItem>
            <SelectItem value="pro">Pro</SelectItem>
            <SelectItem value="studio">Studio</SelectItem>
            <SelectItem value="legacy">Legacy</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Photographers Table */}
      <Card className="bg-card border-border">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left p-4 text-sm font-medium text-muted-foreground">Photographer</th>
                  <th className="text-left p-4 text-sm font-medium text-muted-foreground">Plan</th>
                  <th className="text-left p-4 text-sm font-medium text-muted-foreground">Status</th>
                  <th className="text-left p-4 text-sm font-medium text-muted-foreground">Bookings</th>
                  <th className="text-left p-4 text-sm font-medium text-muted-foreground">
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger className="flex items-center gap-1">
                          Revenue
                          <HelpCircle className="w-3 h-3 text-muted-foreground" />
                        </TooltipTrigger>
                        <TooltipContent>
                          <p className="max-w-xs text-xs">Total gallery/photo sales revenue generated by this photographer (completed purchases only)</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </th>
                  <th className="text-right p-4 text-sm font-medium text-muted-foreground">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredPhotographers.map((photographer, index) => {
                  const status = getStatusBadge(photographer.subscription_status);
                  return (
                    <motion.tr
                      key={photographer.photographer_id}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: index * 0.03 }}
                      className="border-b border-border last:border-0 hover:bg-muted/30"
                    >
                      <td className="p-4">
                        <div className="flex items-center gap-3">
                          <img
                            src={
                              photographer.user?.picture 
                                ? (photographer.user.picture.startsWith('/api/') 
                                    ? `${API.replace('/api', '')}${photographer.user.picture}`
                                    : photographer.user.picture)
                                : "/placeholder.png"
                            }
                            alt={photographer.user?.name}
                            className="w-10 h-10 rounded-sm object-cover"
                          />
                          <div>
                            <p className="font-medium">{photographer.user?.name}</p>
                            <p className="text-sm text-muted-foreground">{photographer.user?.email}</p>
                          </div>
                        </div>
                      </td>
                      <td className="p-4">
                        <Badge variant="outline" className="capitalize">
                          {photographer.current_plan_key}
                        </Badge>
                      </td>
                      <td className="p-4">
                        <Badge className={`${status.className} border`}>
                          {status.label}
                        </Badge>
                      </td>
                      <td className="p-4 text-muted-foreground">
                        {photographer.stats?.bookings || 0}
                      </td>
                      <td className="p-4">
                        ${photographer.stats?.revenue?.toFixed(2) || "0.00"}
                      </td>
                      <td className="p-4 text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm">
                              <MoreVertical className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => {
                              setSelectedPhotographer(photographer);
                              setDetailDialogOpen(true);
                            }}>
                              <Eye className="w-4 h-4 mr-2" />
                              View Details
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleImpersonate(photographer)}>
                              <ExternalLink className="w-4 h-4 mr-2" />
                              Impersonate
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => {
                              setSelectedPhotographer(photographer);
                              setActionType("plan_change");
                              setActionDialogOpen(true);
                            }}>
                              <ArrowUpDown className="w-4 h-4 mr-2" />
                              Change Plan
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => openFounderDialog(photographer)}>
                              <Crown className="w-4 h-4 mr-2" />
                              Set Founder Status
                            </DropdownMenuItem>
                            {photographer.subscription_status !== "suspended" ? (
                              <DropdownMenuItem 
                                onClick={() => {
                                  setSelectedPhotographer(photographer);
                                  setActionType("suspended");
                                  setActionDialogOpen(true);
                                }}
                                className="text-red-400"
                              >
                                <UserX className="w-4 h-4 mr-2" />
                                Suspend
                              </DropdownMenuItem>
                            ) : (
                              <DropdownMenuItem 
                                onClick={() => {
                                  setSelectedPhotographer(photographer);
                                  setActionType("active");
                                  setActionDialogOpen(true);
                                }}
                                className="text-green-400"
                              >
                                <UserCheck className="w-4 h-4 mr-2" />
                                Reactivate
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuItem 
                              onClick={() => {
                                setSelectedPhotographer(photographer);
                                setDeleteDialogOpen(true);
                              }}
                              className="text-red-500"
                            >
                              <Trash2 className="w-4 h-4 mr-2" />
                              Delete Account
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                    </motion.tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          
          {filteredPhotographers.length === 0 && (
            <div className="py-12 text-center text-muted-foreground">
              No photographers found
            </div>
          )}
        </CardContent>
      </Card>

      {/* Detail Dialog */}
      <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">Photographer Details</DialogTitle>
          </DialogHeader>
          
          {selectedPhotographer && (
            <div className="space-y-6">
              <div className="flex items-center gap-4">
                <img
                  src={selectedPhotographer.user?.picture || "/placeholder.png"}
                  alt={selectedPhotographer.user?.name}
                  className="w-16 h-16 rounded-sm object-cover"
                />
                <div>
                  <h3 className="text-xl font-medium">{selectedPhotographer.user?.name}</h3>
                  <p className="text-muted-foreground">{selectedPhotographer.user?.email}</p>
                </div>
              </div>

              <div className="grid sm:grid-cols-3 gap-4">
                <div className="p-4 bg-muted/30 rounded-sm">
                  <p className="text-sm text-muted-foreground">Plan</p>
                  <p className="text-lg font-medium capitalize">{selectedPhotographer.current_plan_key}</p>
                </div>
                <div className="p-4 bg-muted/30 rounded-sm">
                  <p className="text-sm text-muted-foreground">Status</p>
                  <p className="text-lg font-medium capitalize">{selectedPhotographer.subscription_status}</p>
                </div>
                <div className="p-4 bg-muted/30 rounded-sm">
                  <p className="text-sm text-muted-foreground flex items-center gap-1">
                    Founder Status
                    {selectedPhotographer.founder_tier && selectedPhotographer.founder_tier !== "none" && (
                      <Crown className="w-4 h-4 text-amber-400" />
                    )}
                  </p>
                  <p className="text-lg font-medium capitalize">
                    {selectedPhotographer.founder_tier?.replace("_", " ") || "None"}
                  </p>
                </div>
              </div>

              <div className="grid sm:grid-cols-3 gap-4">
                <div className="p-4 bg-muted/30 rounded-sm">
                  <p className="text-sm text-muted-foreground flex items-center gap-1">
                    Gallery Sales Revenue
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger>
                          <HelpCircle className="w-3 h-3" />
                        </TooltipTrigger>
                        <TooltipContent>
                          <p className="max-w-xs text-xs">Total revenue from photo/gallery purchases by their clients</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </p>
                  <p className="text-lg font-medium">${selectedPhotographer.stats?.revenue?.toFixed(2) || "0.00"}</p>
                </div>
                <div className="p-4 bg-muted/30 rounded-sm">
                  <p className="text-sm text-muted-foreground">Bookings</p>
                  <p className="text-lg font-medium">{selectedPhotographer.stats?.bookings || 0}</p>
                </div>
                <div className="p-4 bg-muted/30 rounded-sm">
                  <p className="text-sm text-muted-foreground">Galleries</p>
                  <p className="text-lg font-medium">{selectedPhotographer.stats?.galleries || 0}</p>
                </div>
              </div>

              <div>
                <h4 className="font-medium mb-2">Bio</h4>
                <p className="text-muted-foreground">{selectedPhotographer.bio || "No bio set"}</p>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setDetailDialogOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Action Dialog */}
      <Dialog open={actionDialogOpen} onOpenChange={setActionDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">
              {actionType === "suspended" && "Suspend Photographer"}
              {actionType === "active" && "Reactivate Photographer"}
              {actionType === "plan_change" && "Change Plan"}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {actionType === "plan_change" && (
              <div>
                <Label>New Plan</Label>
                <Select 
                  value={actionData.plan_key} 
                  onValueChange={(value) => setActionData(prev => ({ ...prev, plan_key: value }))}
                >
                  <SelectTrigger className="mt-2">
                    <SelectValue placeholder="Select plan" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="starter">Starter ($19/mo)</SelectItem>
                    <SelectItem value="pro">Pro ($39/mo)</SelectItem>
                    <SelectItem value="studio">Studio ($79/mo)</SelectItem>
                    <SelectItem value="legacy">Legacy ($10/mo) - Admin Only</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            {actionType === "suspended" && (
              <div>
                <Label>Reason (optional)</Label>
                <Textarea
                  value={actionData.reason}
                  onChange={(e) => setActionData(prev => ({ ...prev, reason: e.target.value }))}
                  placeholder="Reason for suspension..."
                  className="mt-2"
                />
              </div>
            )}

            {actionType === "active" && (
              <p className="text-muted-foreground">
                This will reactivate the photographer&apos;s account and restore access.
              </p>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setActionDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleAction} data-testid="confirm-action">
              {actionType === "suspended" && "Suspend Account"}
              {actionType === "active" && "Reactivate Account"}
              {actionType === "plan_change" && "Update Plan"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl text-red-500 flex items-center gap-2">
              <AlertTriangle className="w-6 h-6" />
              Delete Account Permanently
            </DialogTitle>
          </DialogHeader>
          
          {selectedPhotographer && (
            <div className="space-y-4">
              <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-lg">
                <p className="text-red-400 font-medium mb-2">⚠️ This action cannot be undone!</p>
                <p className="text-sm text-muted-foreground">
                  You are about to permanently delete the account for:
                </p>
                <div className="mt-3 flex items-center gap-3">
                  <img
                    src={selectedPhotographer.user?.picture || "/placeholder.png"}
                    alt=""
                    className="w-10 h-10 rounded-full"
                  />
                  <div>
                    <p className="font-medium">{selectedPhotographer.user?.name}</p>
                    <p className="text-sm text-muted-foreground">{selectedPhotographer.user?.email}</p>
                  </div>
                </div>
              </div>
              
              <div className="text-sm text-muted-foreground">
                <p className="font-medium text-foreground mb-2">The following data will be permanently deleted:</p>
                <ul className="list-disc list-inside space-y-1">
                  <li>User account and profile</li>
                  <li>All galleries and uploaded photos</li>
                  <li>All booking requests and quotes</li>
                  <li>All invoices and payment records</li>
                  <li>All contracts and templates</li>
                  <li>Promo codes and usage data</li>
                  <li>Calendar connections and availability</li>
                  <li>All client session data</li>
                </ul>
              </div>
              
              <p className="text-sm text-muted-foreground">
                An email will be sent to notify the user of the deletion.
              </p>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)} disabled={deleting}>
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleDelete}
              disabled={deleting}
              data-testid="confirm-delete"
            >
              {deleting ? "Deleting..." : "Delete Permanently"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Founder Status Dialog */}
      <Dialog open={founderDialogOpen} onOpenChange={setFounderDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl flex items-center gap-2">
              <Crown className="w-6 h-6 text-amber-400" />
              Set Founder Status
            </DialogTitle>
          </DialogHeader>
          
          {selectedPhotographer && (
            <div className="space-y-4">
              <div className="flex items-center gap-3 p-3 bg-muted/30 rounded-lg">
                <img
                  src={selectedPhotographer.user?.picture || "/placeholder.png"}
                  alt=""
                  className="w-10 h-10 rounded-full"
                />
                <div>
                  <p className="font-medium">{selectedPhotographer.user?.name}</p>
                  <p className="text-sm text-muted-foreground">{selectedPhotographer.user?.email}</p>
                </div>
              </div>
              
              <div>
                <Label className="mb-2 block">Founder Tier</Label>
                <Select value={founderTier} onValueChange={setFounderTier}>
                  <SelectTrigger data-testid="founder-tier-select">
                    <SelectValue placeholder="Select tier" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None (Remove Status)</SelectItem>
                    <SelectItem value="early_access">Early Access (+15 video min)</SelectItem>
                    <SelectItem value="founder">Founder (+30 video min)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="text-sm text-muted-foreground space-y-1">
                <p><strong>Benefits:</strong></p>
                <ul className="list-disc list-inside">
                  <li>Early Access: +15 bonus video minutes</li>
                  <li>Founder: +30 bonus video minutes + price lock</li>
                </ul>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setFounderDialogOpen(false)} disabled={savingFounder}>
              Cancel
            </Button>
            <Button 
              onClick={handleFounderStatus}
              disabled={savingFounder}
              data-testid="confirm-founder-status"
            >
              {savingFounder ? "Saving..." : "Update Status"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminPhotographers;
